import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/lilia-ui/select";
import { BlockEditModal } from "../BlockEditModal";
import { CodeEditor } from "../blocks/CodeEditor";
import styles from "../blocks/blocks.module.scss";
import { testIds } from "@/testIds";

const LANGUAGES = [
  "plaintext", "javascript", "typescript", "python", "java", "c", "cpp",
  "csharp", "go", "rust", "ruby", "php", "swift", "kotlin", "sql",
  "html", "css", "json", "yaml", "xml", "bash", "powershell",
  "latex", "markdown", "r", "matlab", "haskell", "scala",
];

export function CodeNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const code = (node.attrs.code as string) || "";
  // As stored: "" is a verbatim block. Shown as plaintext, but kept as it is
  // unless the author picks a language.
  const storedLanguage = (node.attrs.language as string | null) ?? "plaintext";
  const language = storedLanguage || "plaintext";

  const [isOpen, setIsOpen] = useState(false);
  const [draftCode, setDraftCode] = useState(code);
  const [draftLanguage, setDraftLanguage] = useState(storedLanguage);

  const handleOpen = useCallback(() => {
    setDraftCode(code);
    setDraftLanguage(storedLanguage);
    setIsOpen(true);
  }, [code, storedLanguage]);

  const handleSave = useCallback(() => {
    updateAttributes({ code: draftCode, language: draftLanguage });
    setIsOpen(false);
  }, [draftCode, draftLanguage, updateAttributes]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <NodeViewWrapper data-type="code-block-node" data-testid={testIds.code.block} data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={styles.code}
        data-testid={testIds.code.preview}
        onClick={handleOpen}
        onContextMenu={handleContextMenu}
        title="Click to edit code"
      >
        <div className={styles.codeHeader}>
          <span className={styles.codeLanguage}>{language}</span>
        </div>
        <pre className={styles.codePreview}>
          {code || "(empty code block)"}
        </pre>
      </div>

      <BlockEditModal
        sourceNode={node}
        title="Edit Code Block"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={760}
      >
        <div className={styles.codeModalContent} data-testid={testIds.code.editSource}>
          <div style={{ marginBottom: 12, flexShrink: 0 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Language</label>
            <Select value={draftLanguage || "plaintext"} onValueChange={(val) => setDraftLanguage(val || "plaintext")}>
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <CodeEditor
            value={draftCode}
            onChange={(val) => setDraftCode(val)}
            language={draftLanguage || "plaintext"}
          />
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
